import {
  faArrowLeft,
  faBoxArchive,
  faExclamationCircle,
  faLock,
  faQuestionCircle,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { twJoin } from "tailwind-merge";

import { DocumentTitle } from "src/base-components/DocumentTitle";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import {
  ResizableSidepaneProvider,
  ResizeHandle,
  useResizableSidepane,
} from "src/base-components/ResizableSidepane";
import { CaseBlockScrollProvider } from "src/cases/caseView/CaseBlockScrollContext";
import { CaseBlocks } from "src/cases/caseView/CaseBlocks";
import { CasePageHeaderSideContent } from "src/cases/caseView/CasePageHeaderSideContent";
import { CasePageHeaderTitle } from "src/cases/caseView/CasePageHeaderTitle";
import { CaseSidebar } from "src/cases/caseView/CaseSidebar";
import { CaseWindowManager } from "src/cases/caseView/CaseWindowManager";
import { useCasePollInterval } from "src/cases/caseView/useCasePollInterval";
import { useCase } from "src/cases/queries/cases";
import { useCaseQueue, useSiblingCases } from "src/cases/queries/queues";
import { useCaseTemplateVersionForCase } from "src/cases/queries/templates";
import { useCaseBackNavigation } from "src/cases/useCaseBackNavigation";
import { useCaseContextParams } from "src/cases/useCaseContextParams";
import { EmptyState } from "src/design-system/EmptyState";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import { PageHeader } from "src/layout/PageHeader";
import { CasePageParams, getUrlToCaseViewPage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const CasePageContent: React.FC = () => {
  const { width, isResizing } = useResizableSidepane();
  const { orgId, wsId, case_id: caseId } = useParamsDecode<CasePageParams>();
  const { queueId: urlQueueId, filterParams, sort } = useCaseContextParams();
  const searchParams = { ...filterParams, ...(sort ? { sort } : {}) };
  const pollInterval = useCasePollInterval();

  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
    refetch: refetchCase,
  } = useCase(caseId, {
    // It is enough to set refetchInterval at the top level hook
    // because all the child queries will inherit it
    refetchInterval: pollInterval,
  });

  const { data: templateVersion } = useCaseTemplateVersionForCase({
    templateId: caseData?.data.template?.id,
    versionId: caseData?.data.template_version?.id,
    templateVersionEtag: caseData?.data.template_version_etag,
  });

  // Fall back to the embedded copy so the page renders immediately on first load.
  const blocksConfig =
    templateVersion?.attributes.case_blocks_configs ??
    caseData?.data.template_version?.case_blocks_configs;

  const queueId = caseData?.data.queue?.id ?? urlQueueId;
  const { data: queueData } = useCaseQueue(queueId);

  const { backLink, titleLabel } = useCaseBackNavigation({
    orgId,
    wsId,
    caseId,
    queueId,
    queueName: queueData?.name,
    environment: (caseData?.data.env as Environment) ?? "live",
    searchParams,
  });

  const { prevCaseId, nextCaseId } = useSiblingCases({
    currentCaseId: caseId,
    queueId,
    environment: (caseData?.data.env as Environment) ?? "live",
    filters: filterParams,
  });

  const buildCaseUrl = (targetCaseId: string) =>
    getUrlToCaseViewPage(orgId, wsId, targetCaseId, {
      ...searchParams,
      ...(queueId ? { queueId } : {}),
    });

  const navigation = {
    previous: {
      url: prevCaseId ? buildCaseUrl(prevCaseId) : "",
      disabled: !prevCaseId,
      tooltip: "Previous case",
    },
    next: {
      url: nextCaseId ? buildCaseUrl(nextCaseId) : "",
      disabled: !nextCaseId,
      tooltip: "Next case",
    },
  };

  if (caseError) {
    return <CaseLoadingErrorPage error={caseError} onRetry={refetchCase} />;
  }

  return (
    <CaseBlockScrollProvider>
      <CaseWindowManager />
      <div
        className="flex h-screen flex-col bg-gray-50"
        data-loc="case-page-content"
      >
        <PageHeader
          backLink={backLink}
          navigation={navigation}
          sideContent={
            <CasePageHeaderSideContent
              caseData={caseData}
              isLoading={caseLoading}
            />
          }
          title={
            <CasePageHeaderTitle
              caseData={caseData}
              isLoading={caseLoading}
              contextLabel={titleLabel}
            />
          }
        />
        <div
          className={twJoin(
            "relative m-auto flex h-full min-h-0 w-full items-stretch bg-gray-50",
            isResizing && "cursor-col-resize select-none",
          )}
        >
          <div className="h-full min-w-0 flex-1">
            {caseData?.data.case_blocks && blocksConfig && (
              <CaseBlocks
                blocks={caseData.data.case_blocks}
                blocksConfig={blocksConfig}
                caseData={caseData}
                caseEnv={caseData.data.env}
              />
            )}
          </div>
          <div
            className="relative border-gray-200 border-l bg-white"
            style={{ width: `${width}px` }}
            data-loc="case-sidebar-container"
          >
            <ResizeHandle />
            <CaseSidebar />
          </div>
        </div>
      </div>
    </CaseBlockScrollProvider>
  );
};

export const CaseViewPage: React.FC = () => {
  return (
    <FloatingWindowsProvider>
      <DocumentTitle segments={["Case Details", "Cases"]} />
      <ResizableSidepaneProvider>
        <CasePageContent />
      </ResizableSidepaneProvider>
    </FloatingWindowsProvider>
  );
};

type CaseLoadingErrorPageProps = {
  error: unknown;
  onRetry: () => void;
};

type ErrorContent = {
  icon: typeof faExclamationCircle;
  headline: string;
  description: string;
  buttonType: "retry" | "backToQueues" | "none";
};

const getErrorContent = (error: unknown): ErrorContent => {
  if (isAxiosError(error)) {
    const status = error.response?.status;

    switch (status) {
      case 404:
        return {
          icon: faQuestionCircle,
          headline: "Case not found",
          description:
            "The case you're looking for doesn't exist or may have been deleted.",
          buttonType: "backToQueues",
        };
      case 403:
        return {
          icon: faLock,
          headline: "Access denied",
          description:
            "You don't have permission to view this case. Please contact your administrator if you believe this is an error.",
          buttonType: "backToQueues",
        };
      case 409:
        return {
          icon: faExclamationCircle,
          headline: "Case is outdated",
          description:
            "This case was created before breaking changes were made to the case template.",
          buttonType: "backToQueues",
        };
      case 410:
        return {
          icon: faBoxArchive,
          headline: "Case archived",
          description: "This case has been archived.",
          buttonType: "backToQueues",
        };
      default:
        break;
    }
  }

  return {
    icon: faExclamationCircle,
    headline: "Error loading case",
    description:
      "We're having trouble loading this case. Please try again later or contact Taktile support if the problem persists.",
    buttonType: "retry",
  };
};

const CaseLoadingErrorPage: React.FC<CaseLoadingErrorPageProps> = ({
  error,
  onRetry,
}) => {
  const { orgId, wsId, case_id: caseId } = useParamsDecode<CasePageParams>();
  const { queueId, filterParams, sort } = useCaseContextParams();
  const [environment] = useEnvironment();
  const searchParams = { ...filterParams, ...(sort ? { sort } : {}) };
  const errorContent = getErrorContent(error);

  const { backLink, backToLabel } = useCaseBackNavigation({
    orgId,
    wsId,
    caseId,
    queueId,
    queueName: undefined,
    environment,
    searchParams,
  });

  const getActionButton = () => {
    switch (errorContent.buttonType) {
      case "retry":
        return {
          label: "Try again",
          icon: faRefresh,
          onClick: onRetry,
        };
      case "backToQueues":
        return {
          label: backToLabel,
          icon: faArrowLeft,
          linkTo: backLink,
        };
      case "none":
      default:
        return undefined;
    }
  };

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      <PageHeader backLink={backLink} title={backToLabel} />
      <div className="flex h-full w-full items-center justify-center">
        <EmptyState
          actionButton={getActionButton()}
          dataLoc="case-loading-error"
          description={errorContent.description}
          headline={errorContent.headline}
          icon={errorContent.icon}
          variant="error"
        />
      </div>
    </div>
  );
};
